import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Camera, RotateCcw, Square, Plus, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

interface CameraCaptureProps {
  onClose: () => void;
  onCapture: (mediaFiles: File[]) => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onClose, onCapture }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImages, setCapturedImages] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isVerified, setIsVerified] = useState(false);
  const [mode, setMode] = useState<'photo' | 'video'>('photo');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    // Check if user is verified by checking their role and is_verified flag
    const checkVerification = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('is_verified, role')
            .eq('id', user.id)
            .single();
          
          if (data && !error) {
            // Auto-verify vendors or use existing verification status
            setIsVerified(data.is_verified || data.role === 'vendor');
          }
        } catch (error) {
          console.error('Error checking verification:', error);
          setIsVerified(false);
        }
      }
    };
    checkVerification();
    
    // Don't start camera immediately, let user choose mode first
  }, [user]);

  useEffect(() => {
    startCamera();
    return () => {
      // Stop all tracks to release camera/mic
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [facingMode, mode]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      console.log('Starting camera with facingMode:', facingMode, 'mode:', mode);
      
      // Stop existing stream first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const constraints = {
        video: {
          facingMode,
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 }
        },
        audio: mode === 'video' // Only request audio for video mode
      };
      
      console.log('Requesting media with constraints:', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Got media stream:', mediaStream, 'video tracks:', mediaStream.getVideoTracks().length);
      
      setStream(mediaStream);
      if (videoRef.current) {
        console.log('Setting srcObject to video element');
        videoRef.current.srcObject = mediaStream;
        
        // Wait for loadedmetadata before playing
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, attempting to play');
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => console.log('Video playing successfully'))
              .catch(error => console.error('Video play failed:', error));
          }
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setCapturedImages(prev => [...prev, file]);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const startVideoRecording = () => {
    if (!stream) return;

    // Try to use MP4 codec for better mobile compatibility
    let options = {};
    if (MediaRecorder.isTypeSupported('video/mp4; codecs="avc1.42E01E"')) {
      options = { mimeType: 'video/mp4; codecs="avc1.42E01E"' };
      console.log('Using MP4 codec for recording');
    } else if (MediaRecorder.isTypeSupported('video/webm; codecs="vp8,opus"')) {
      options = { mimeType: 'video/webm; codecs="vp8,opus"' };
      console.log('Using WebM codec for recording');
    } else {
      console.log('Using default codec for recording');
    }

    const mediaRecorder = new MediaRecorder(stream, options);
    const chunks: Blob[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const videoBlob = new Blob(chunks, { type: 'video/mp4' });
      const file = new File([videoBlob], `video_${Date.now()}.mp4`, { type: 'video/mp4' });
      console.log('Video recorded:', file.name, file.type, file.size, 'bytes');
      onCapture([file]);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    setRecordingDuration(0);

    recordingIntervalRef.current = setInterval(() => {
      setRecordingDuration(prev => {
        if (prev >= 10) {
          stopVideoRecording();
          return 10;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const removeImage = (index: number) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleGalleryUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onCapture(files);
    }
  };

  const handleContinue = () => {
    if (capturedImages.length > 0) {
      onCapture(capturedImages);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50">
      <div className="relative h-full">
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </Button>
          
          {/* Mode Selection */}
          <div className="flex bg-black/30 rounded-full p-1">
            <button
              onClick={() => setMode('photo')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                mode === 'photo' ? 'bg-white text-black' : 'text-white'
              }`}
            >
              Photo
            </button>
            <button
              onClick={() => setMode('video')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                mode === 'video' ? 'bg-white text-black' : 'text-white'
              }`}
            >
              Video
            </button>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={switchCamera}
            className="text-white hover:bg-white/20"
          >
            <RotateCcw className="h-6 w-6" />
          </Button>
        </div>

        {/* Camera View */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          controls={false}
          style={{ objectFit: 'cover' }}
          onLoadedMetadata={(e) => {
            console.log('Video loadedmetadata event fired');
            const video = e.target as HTMLVideoElement;
            video.play()
              .then(() => console.log('Manual play successful'))
              .catch(error => console.error('Manual play failed:', error));
          }}
          onCanPlay={() => console.log('Video can play')}
          onPlaying={() => console.log('Video is playing')}
          onError={(e) => console.error('Video error:', e)}
          className={`w-full h-full ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
        />

        {/* Recording Duration */}
        {isRecording && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-full">
            {recordingDuration}s / 10s
          </div>
        )}

        {/* Captured Images Preview */}
        {capturedImages.length > 0 && (
          <div className="absolute top-20 left-4 right-4">
            <div className="flex gap-2 overflow-x-auto">
              {capturedImages.map((file, index) => (
                <div key={index} className="relative flex-shrink-0">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Captured ${index + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-white"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-6 h-6"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-8 left-0 right-0">
          <div className="flex justify-between items-center px-8">
            {/* Gallery Access for Verified Users */}
            {isVerified ? (
              <div>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="hidden"
                  id="gallery-upload"
                />
                <label htmlFor="gallery-upload">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 w-16 h-16"
                    asChild
                  >
                    <div>
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="w-16 h-16"></div>
            )}

            {/* Capture/Record Button - Centered */}
            <Button
              variant="ghost"
              size="icon"
              onClick={mode === 'photo' ? capturePhoto : (isRecording ? stopVideoRecording : startVideoRecording)}
              disabled={mode === 'photo' && capturedImages.length >= 1}
              className={`text-white hover:bg-white/20 w-20 h-20 rounded-full border-4 border-white ${
                mode === 'photo' && capturedImages.length >= 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isRecording ? <Square className="h-8 w-8" /> : <Camera className="h-8 w-8" />}
            </Button>

            {/* Right placeholder for symmetry */}
            <div className="w-16 h-16"></div>
          </div>

          {/* Continue Button */}
          {capturedImages.length > 0 && (
            <div className="mt-4 flex justify-center">
              <Button
                onClick={handleContinue}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full"
              >
                Continue ({capturedImages.length})
              </Button>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default CameraCapture;